import jsPDF from 'jspdf';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { formatPeriod } from './formatters';

/**
 * Gera relatório em PDF com layout profissional similar ao web
 * @param {Array} dados - Array de dados das franquias
 * @param {string} periodo - Período do relatório
 * @param {Object} resumo - Dados do resumo do período (opcional)
 */
export const exportarPDF = (dados, periodo, resumo = null) => {
  const doc = new jsPDF();
  
  // Cores do tema
  const colors = {
    primary: [59, 130, 246],    // blue-500
    secondary: [107, 114, 128], // gray-500
    success: [34, 197, 94],     // green-500
    danger: [239, 68, 68],      // red-500
    warning: [245, 158, 11],    // amber-500
    text: [31, 41, 55],         // gray-800
    light: [243, 244, 246]      // gray-100
  };
  
  // Configurações de fonte
  doc.setFont('helvetica');
  
  // Cabeçalho com fundo colorido
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, 210, 40, 'F');
  
  // Título branco no cabeçalho
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Relatório de Franquias', 105, 20, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Período: ${formatPeriod(periodo)}`, 105, 28, { align: 'center' });
  
  const dataHoraGeracao = new Date();
  const dataFormatada = dataHoraGeracao.toLocaleDateString('pt-BR');
  const horaFormatada = dataHoraGeracao.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  doc.text(`Gerado em: ${dataFormatada} às ${horaFormatada}`, 105, 35, { align: 'center' });
  
  // Resetar cor do texto
  doc.setTextColor(...colors.text);
  
  // Calcular estatísticas usando resumo se disponível, senão usar dados
  let totalFranquias, totalWil, totalPictor, totalGeral, mediaPorFranquia;
  
  if (resumo) {
    // Usar dados do resumo que considera apenas franquias que existiam no período
    totalFranquias = Number(resumo.total_franquias_ativas) || 0;
    totalWil = Number(resumo.total_wil_ativas) || 0;
    totalPictor = Number(resumo.total_pictor_ativas) || 0;
    totalGeral = totalWil + totalPictor;
    mediaPorFranquia = totalFranquias > 0 ? Math.round(totalGeral / totalFranquias) : 0;
  } else {
    // Fallback: calcular dos dados
    totalFranquias = dados.length;
    totalWil = dados.reduce((acc, item) => acc + (item.total_wil || 0), 0);
    totalPictor = dados.reduce((acc, item) => acc + (item.total_pictor || 0), 0);
    totalGeral = totalWil + totalPictor;
    mediaPorFranquia = totalFranquias > 0 ? Math.round(totalGeral / totalFranquias) : 0;
  }
  
  // Estatísticas em cards
  const stats = [
    { label: 'Total de Franquias', value: totalFranquias, color: colors.primary },
    { label: 'Total WIL', value: totalWil, color: colors.success },
    { label: 'Total Pictor', value: totalPictor, color: colors.warning },
    { label: 'Média por Franquia', value: mediaPorFranquia, color: colors.danger }
  ];
  
  // Cards de estatísticas
  let cardY = 50;
  const cardWidth = 40;
  const cardHeight = 25;
  const cardSpacing = 6;
  
  // Calcular largura total e centralizar
  const totalWidth = (cardWidth * 4) + (cardSpacing * 3);
  const startX = (210 - totalWidth) / 2; // Centralizar na página (210mm de largura)
  
  stats.forEach((stat, index) => {
    const x = startX + (index * (cardWidth + cardSpacing));
    
    // Fundo do card
    doc.setFillColor(...stat.color);
    doc.roundedRect(x, cardY, cardWidth, cardHeight, 3, 3, 'F');
    
    // Texto do card
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.label, x + cardWidth/2, cardY + 10, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text(String(stat.value), x + cardWidth/2, cardY + 18, { align: 'center' });
  });
  
  // Resetar cor do texto
  doc.setTextColor(...colors.text);
  
  // Tabela de dados
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Dados Detalhados', 15, 90);
  
  // Cabeçalho da tabela
  doc.setFontSize(10);
  doc.setFillColor(...colors.light);
  doc.rect(15, 95, 180, 8, 'F');
  
  doc.setTextColor(...colors.text);
  const headers = ['Franquia', 'Status', 'WIL', 'Pictor', 'Total'];
  const headerX = [15, 60, 100, 130, 160];
  
  headers.forEach((header, index) => {
    doc.setFont('helvetica', 'bold');
    doc.text(header, headerX[index], 100);
  });
  
  // Linha separadora
  doc.setDrawColor(...colors.secondary);
  doc.setLineWidth(0.5);
  doc.line(15, 104, 195, 104);
  
  // Dados da tabela
  doc.setFont('helvetica', 'normal');
  let currentY = 110;
  const rowHeight = 8;
  const maxRowsPerPage = 20;
  let rowCount = 0;
  let pageNumber = 1;
  
  // Função para adicionar rodapé
  const addFooter = (currentPage) => {
    doc.setFillColor(...colors.light);
    doc.rect(0, 280, 210, 17, 'F');
    
    doc.setTextColor(...colors.secondary);
    doc.setFontSize(8);
    doc.text('Dashboard Inviolável - Sistema de Gerenciamento de Franquias', 105, 287, { align: 'center' });
    doc.text(`Página ${currentPage}`, 105, 292, { align: 'center' });
  };
  
  const checkNewPage = () => {
    if (rowCount >= maxRowsPerPage) {
      // Adicionar rodapé na página atual antes de criar nova
      addFooter(pageNumber);
      pageNumber++;
      
      doc.addPage();
      
      // Repetir cabeçalho na nova página
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório de Franquias', 105, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.text(`${formatPeriod(periodo)}`, 105, 30, { align: 'center' });
      
      // Cabeçalho da tabela
      doc.setTextColor(...colors.text);
      doc.setFillColor(...colors.light);
      doc.rect(15, 50, 180, 8, 'F');
      headers.forEach((header, headerIndex) => {
        doc.setFont('helvetica', 'bold');
        doc.text(header, headerX[headerIndex], 55);
      });
      doc.setDrawColor(...colors.secondary);
      doc.setLineWidth(0.5);
      doc.line(15, 59, 195, 59);
      
      currentY = 65;
      rowCount = 0;
    }
  };
  
  dados.forEach((item, index) => {
    checkNewPage();
    
    // Linha da Franquia (em negrito)
    doc.setFont('helvetica', 'bold');
    const nomeFranquia = item.nome_franquia.length > 25 
      ? item.nome_franquia.substring(0, 22) + '...' 
      : item.nome_franquia;
    doc.text(nomeFranquia, headerX[0], currentY);
    
    // Status com cor
    const status = item.status_franquia === 'A' ? 'Ativa' : 
                   item.status_franquia === 'I' ? 'Inativa' : 'Bloqueada';
    const statusColor = item.status_franquia === 'A' ? colors.success : 
                       item.status_franquia === 'I' ? colors.danger : colors.warning;
    
    doc.setTextColor(...statusColor);
    doc.text(status, headerX[1], currentY);
    
    // Valores numéricos da franquia
    doc.setTextColor(...colors.text);
    doc.text(String(item.total_wil || 0), headerX[2], currentY);
    doc.text(String(item.total_pictor || 0), headerX[3], currentY);
    
    const total = (item.total_wil || 0) + (item.total_pictor || 0);
    doc.text(String(total), headerX[4], currentY);
    
    currentY += rowHeight;
    rowCount++;
    
    // Empresas da franquia (se houver)
    if (item.empresas && item.empresas.length > 0) {
      item.empresas.forEach(empresa => {
        checkNewPage();
        
        // Nome da empresa (indentado)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120); // Cinza mais claro
        
        const nomeEmpresa = empresa.nome_empresa.length > 28 
          ? empresa.nome_empresa.substring(0, 25) + '...' 
          : empresa.nome_empresa;
        
        // Usar posição absoluta sem caracteres especiais
        doc.text(nomeEmpresa, headerX[0] + 5, currentY);
        
        // Valores da empresa
        doc.setTextColor(...colors.text);
        doc.setFontSize(10);
        doc.text(String(empresa.total_locais || 0), headerX[2], currentY);
        doc.text(String(empresa.total_cftv || 0), headerX[3], currentY);
        
        const totalEmpresa = (empresa.total_locais || 0) + (empresa.total_cftv || 0);
        doc.text(String(totalEmpresa), headerX[4], currentY);
        
        currentY += rowHeight;
        rowCount++;
      });
    }
  });
  
  // Adicionar rodapé na última página
  addFooter(pageNumber);
  
  // Salva o PDF
  const fileName = `relatorio-franquias-${periodo}.pdf`;
  doc.save(fileName);
};

/**
 * Gera relatório em CSV com dados das franquias (formato de colunas separadas)
 * @param {Array} dados - Array de dados das franquias
 * @param {string} periodo - Período do relatório
 * @param {Object} resumo - Dados do resumo do período (opcional)
 */
export const exportarCSV = (dados, periodo, resumo = null) => {
  // Prepara os dados para o CSV (franquias + empresas)
  const csvData = [];
  
  // Adiciona cabeçalho
  csvData.push([
    'Franquia',
    'Empresa',
    'CNPJ',
    'Status',
    'WIL',
    'Pictor',
    'Total',
    'Ultima_Atualizacao'
  ]);
  
  dados.forEach(item => {
    const status = item.status_franquia === 'A' ? 'Ativa' : 
                   item.status_franquia === 'I' ? 'Inativa' : 'Bloqueada';
    const total = (item.total_wil || 0) + (item.total_pictor || 0);
    
    // Adiciona linha da franquia
    csvData.push([
      item.nome_franquia || '',
      '', // Empresa vazia para franquia
      '',
      status,
      item.total_wil || 0,
      item.total_pictor || 0,
      total,
      item.datahora_modificacao 
        ? new Date(item.datahora_modificacao).toLocaleDateString('pt-BR')
        : ''
    ]);
    
    // Adiciona linhas das empresas (se houver)
    if (item.empresas && item.empresas.length > 0) {
      item.empresas.forEach(empresa => {
        const totalEmpresa = (empresa.total_locais || 0) + (empresa.total_cftv || 0);
        csvData.push([
          '', // Franquia vazia para empresa
          empresa.nome_empresa || '',
          empresa.cnpj || '',
          '',
          empresa.total_locais || 0,
          empresa.total_cftv || 0,
          totalEmpresa,
          ''
        ]);
      });
    }
  });
  
  // Calcular totais usando resumo se disponível
  let totalWil, totalPictor, totalGeral, totalFranquias, mediaPorFranquia;
  
  if (resumo) {
    totalFranquias = Number(resumo.total_franquias_ativas) || 0;
    totalWil = Number(resumo.total_wil_ativas) || 0;
    totalPictor = Number(resumo.total_pictor_ativas) || 0;
    totalGeral = totalWil + totalPictor;
    mediaPorFranquia = totalFranquias > 0 ? Math.round(totalGeral / totalFranquias) : 0;
  } else {
    totalWil = dados.reduce((acc, item) => acc + (item.total_wil || 0), 0);
    totalPictor = dados.reduce((acc, item) => acc + (item.total_pictor || 0), 0);
    totalGeral = totalWil + totalPictor;
    totalFranquias = dados.length;
    mediaPorFranquia = totalFranquias > 0 ? Math.round(totalGeral / totalFranquias) : 0;
  }
  
  // Adiciona linhas de resumo no final
  csvData.push([
    'TOTAL GERAL',
    '',
    '',
    '',
    totalWil,
    totalPictor,
    totalGeral,
    ''
  ]);
  
  csvData.push([
    `Total de Franquias: ${totalFranquias}`,
    '',
    '',
    '',
    '',
    '',
    `Média por Franquia: ${mediaPorFranquia}`,
    ''
  ]);
  
  // Configuração do CSV com delimitador ponto e vírgula (padrão Excel PT-BR)
  const config = {
    quotes: false, // Remove aspas para melhor formatação no Excel
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ';', // Usa ponto e vírgula para compatibilidade com Excel PT-BR
    header: false, // Já incluímos o cabeçalho manualmente
    newline: '\n',
    skipEmptyLines: false
  };
  
  // Gera o CSV
  const csv = Papa.unparse(csvData, config);
  
  // Cria o blob e faz o download
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `relatorio-franquias-${periodo}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Gera relatório em XLS (Excel) com dados das franquias
 * @param {Array} dados - Array de dados das franquias
 * @param {string} periodo - Período do relatório
 * @param {Object} resumo - Dados do resumo do período (opcional)
 */
export const exportarXLS = (dados, periodo, resumo = null) => {
  // Prepara os dados para a planilha
  const planilhaData = [];
  
  // Adiciona cabeçalho
  planilhaData.push([
    'Franquia',
    'Empresa',
    'CNPJ',
    'Status',
    'WIL',
    'Pictor',
    'Total',
    'Última Atualização'
  ]);
  
  dados.forEach(item => {
    const status = item.status_franquia === 'A' ? 'Ativa' : 
                   item.status_franquia === 'I' ? 'Inativa' : 'Bloqueada';
    const total = (item.total_wil || 0) + (item.total_pictor || 0);
    
    // Adiciona linha da franquia
    planilhaData.push([
      item.nome_franquia || '',
      '', // Empresa vazia para franquia
      '',
      status,
      item.total_wil || 0,
      item.total_pictor || 0,
      total,
      item.datahora_modificacao 
        ? new Date(item.datahora_modificacao).toLocaleDateString('pt-BR')
        : ''
    ]);
    
    // Adiciona linhas das empresas (se houver) - indentadas
    if (item.empresas && item.empresas.length > 0) {
      item.empresas.forEach(empresa => {
        const totalEmpresa = (empresa.total_locais || 0) + (empresa.total_cftv || 0);
        planilhaData.push([
          '', // Franquia vazia para empresa
          empresa.nome_empresa || '',
          empresa.cnpj || '',
          '',
          empresa.total_locais || 0,
          empresa.total_cftv || 0,
          totalEmpresa,
          ''
        ]);
      });
    }
  });
  
  // Calcular totais usando resumo se disponível
  let totalWil, totalPictor, totalGeral, totalFranquias, mediaPorFranquia;
  
  if (resumo) {
    totalFranquias = Number(resumo.total_franquias_ativas) || 0;
    totalWil = Number(resumo.total_wil_ativas) || 0;
    totalPictor = Number(resumo.total_pictor_ativas) || 0;
    totalGeral = totalWil + totalPictor;
    mediaPorFranquia = totalFranquias > 0 ? Math.round(totalGeral / totalFranquias) : 0;
  } else {
    totalWil = dados.reduce((acc, item) => acc + (item.total_wil || 0), 0);
    totalPictor = dados.reduce((acc, item) => acc + (item.total_pictor || 0), 0);
    totalGeral = totalWil + totalPictor;
    totalFranquias = dados.length;
    mediaPorFranquia = totalFranquias > 0 ? Math.round(totalGeral / totalFranquias) : 0;
  }
  
  // Adiciona linhas de resumo no final
  planilhaData.push([
    'TOTAL GERAL',
    '',
    '',
    '',
    totalWil,
    totalPictor,
    totalGeral,
    ''
  ]);
  
  planilhaData.push([
    `Total de Franquias: ${totalFranquias}`,
    '',
    '',
    '',
    '',
    '',
    `Média por Franquia: ${mediaPorFranquia}`,
    ''
  ]);
  
  // Cria a planilha
  const ws = XLSX.utils.aoa_to_sheet(planilhaData);
  
  // Ajusta largura das colunas
  const colWidths = [
    { wch: 35 }, // Franquia
    { wch: 35 }, // Empresa
    { wch: 20 }, // CNPJ
    { wch: 10 }, // Status
    { wch: 8 },  // WIL
    { wch: 8 },  // Pictor
    { wch: 8 },  // Total
    { wch: 18 }  // Última Atualização
  ];
  ws['!cols'] = colWidths;
  
  // Aplicar estilos ao cabeçalho (linha 1) e linha de total
  const range = XLSX.utils.decode_range(ws['!ref']);
  
  // Estilo para cabeçalho (negrito e fundo cinza)
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellAddress]) continue;
    
    ws[cellAddress].s = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: "E0E0E0" } },
      alignment: { horizontal: "center", vertical: "center" }
    };
  }
  
  // Estilo para linha de TOTAL GERAL (penúltima linha)
  const lastRow = range.e.r;
  const totalGeralRow = lastRow - 1;
  
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: totalGeralRow, c: col });
    if (!ws[cellAddress]) continue;
    
    ws[cellAddress].s = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: "FFF3CD" } },
      alignment: { horizontal: col >= 4 ? "right" : "left", vertical: "center" }
    };
  }
  
  // Estilo para linha de estatísticas (última linha)
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: lastRow, c: col });
    if (!ws[cellAddress]) continue;
    
    ws[cellAddress].s = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: "E3F2FD" } },
      alignment: { horizontal: "left", vertical: "center" }
    };
  }
  
  // Estilo para linhas de franquia (negrito)
  for (let row = 1; row <= range.e.r - 2; row++) {
    const cellA = ws[XLSX.utils.encode_cell({ r: row, c: 0 })];
    const cellB = ws[XLSX.utils.encode_cell({ r: row, c: 1 })];
    
    // Se tem valor na coluna Franquia (A) e não tem na coluna Empresa (B), é linha de franquia
    if (cellA && cellA.v && (!cellB || !cellB.v)) {
      // Aplicar negrito em todas as células da linha de franquia
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;
        
        ws[cellAddress].s = {
          font: { bold: true, sz: 10 },
          fill: { fgColor: { rgb: "E3F2FD" } },
          alignment: { horizontal: col >= 4 ? "right" : "left", vertical: "center" }
        };
      }
    }
    // Se tem valor na coluna Empresa (B), é linha de empresa - adicionar indentação visual
    else if (cellB && cellB.v) {
      cellB.s = {
        alignment: { indent: 2, horizontal: "left", vertical: "center" }
      };
    }
  }
  
  // Cria o workbook e adiciona a planilha
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Relatório de Franquias');
  
  // Salva o arquivo XLSX
  XLSX.writeFile(wb, `relatorio-franquias-${periodo}.xlsx`);
};
